/* NETStats in the console.
 *
 * The same public endpoint the desktop page and the marketing page read
 * (/v1/netstats/rankings), rendered here so an operator can look at the
 * world's hosts beside their own connection without leaving the console.
 *
 * NO SESSION IS REQUIRED and none is read. These figures are public by
 * design — a ranking behind a login is not evidence anybody can check —
 * so this page is same-origin and simply fetches. It is the one page in
 * this console with no signed-out state, because there is nothing here
 * that belongs to anybody.
 *
 * Three rules inherited from the API, none of which may be softened here:
 * the fleet size is printed, an unranked target is shown WITH its reason,
 * and an absent component is an em dash rather than a zero.
 */

import { initTheme, bindThemeButton } from "./theme.js";
import { el, statusPill } from "./ui.js";

initTheme();
bindThemeButton();

const $ = (id) => document.getElementById(id);

/* The page's three controls, held here and read by both loaders. The
   view is which list; the order is the host list's (the ISP list is
   always best first). The URL is not consulted: this is a reading
   surface and a reload starting from the default is the honest state. */
const state = { view: "hosts", days: 7, order: "popularity" };

function bindSeg(attr, key, after) {
  for (const btn of document.querySelectorAll(`[${attr}]`)) {
    btn.addEventListener("click", () => {
      state[key] = btn.getAttribute(attr);
      for (const b of document.querySelectorAll(`[${attr}]`)) {
        const on = b === btn;
        b.classList.toggle("active", on);
        if (b.hasAttribute("aria-pressed")) b.setAttribute("aria-pressed", String(on));
        if (b.hasAttribute("aria-selected")) b.setAttribute("aria-selected", String(on));
      }
      after();
    });
  }
}

/* The host view's "not enough vantage points" note, kept so switching
   views can restore it without a refetch. */
let hostsNote = false;

function showView() {
  const hosts = state.view === "hosts";
  $("ns-hosts-card").hidden = !hosts;
  $("ns-isps-card").hidden = hosts;
  $("ns-order-group").hidden = !hosts;
  // The vantage tiles, the "Measured from" card and the vantage floor note
  // belong to the HOST ranking. The ISP list is built from subscribers'
  // own probes and no box of ours takes part in it (isps.ts), so above
  // "Providers, best first" they credited it to machines that did not
  // measure it.
  $("ns-fleet").hidden = !hosts;
  $("ns-vantages-card").hidden = !hosts;
  $("ns-note-card").hidden = !hosts || !hostsNote;
  $("ns-title").textContent = hosts
    ? "The hosts people use, and how they serve"
    : "The providers people pay, and how they serve";
  $("ns-lede").textContent = hosts
    ? "Ordered by how many machines actually reach each host; the score says how well it answers, measured from vantage points Helix operates and weighed with what subscribers’ lines see. A poor score at the top is the row that matters most."
    : "Each provider’s customers’ own probes to the public anchors, folded behind a people floor with dead lines left out, then rated the way every subscriber’s line is rated: calls, gaming, streaming.";
}

const COMPONENTS = ["availability", "latency", "consistency", "stability", "reach"];

function compact(n) {
  if (!Number.isFinite(n)) return "0";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

/// A component cell: the figure, or the em dash that says its evidence was
/// below its floor. Never a zero — zero is a measurement.
function cell(value) {
  const absent = value === null || value === undefined;
  const td = el("td", { class: absent ? "num muted" : "num" });
  td.textContent = absent ? "—" : String(Math.round(Number(value)));
  if (absent) td.title = "below its floor — stated absent, not scored zero";
  return td;
}

/// How the boxes are weighted, said from their own weights.
///
/// This was a fixed paragraph -- "the origin VPS is weighted below the
/// dedicated probes on purpose" -- while production ran two cloud points
/// of presence at ×1 each: a stated method the data contradicted, on the
/// public page. Built from the list it now cannot disagree with it.
const CLASS_PHRASE = { cloud_pop: "a cloud point of presence", origin: "an origin network" };

function weightingSentence(list) {
  if (list.length === 0) return "";
  const weights = list.map((v) => Number(v.weight ?? 1));
  if (weights.every((w) => w === weights[0])) return "Every box counts equally.";
  const byClass = new Map();
  for (const v of list) {
    const set = byClass.get(v.class) ?? new Set();
    set.add(Number(v.weight ?? 1));
    byClass.set(v.class, set);
  }
  if ([...byClass.values()].every((s) => s.size === 1)) {
    const parts = [...byClass].map(([c, s]) => `${CLASS_PHRASE[c] ?? String(c)} counts ×${[...s][0].toFixed(2)}`);
    return `Boxes are weighted by class: ${parts.join(", ")}.`;
  }
  return "Each box carries its own weight in the fold, shown beside it.";
}

/// The console's own tile shape (.stat), not a new one: a second tile
/// style on this surface would be a second thing to keep in step.
function tile(label, value, foot) {
  return el("div", { class: "stat" }, [
    el("span", { class: "stat-label", text: label }),
    el("span", { class: "stat-value", text: String(value) }),
    el("span", { class: "stat-foot" }, [el("span", { text: foot || "" })]),
  ]);
}

async function load() {
  const status = $("ns-status");
  status.textContent = "Fetching the latest published rankings…";
  let data;
  try {
    // Same origin: this page is served by the API that answers it.
    const res = await fetch(`/v1/netstats/rankings?days=${state.days}&order=${state.order}`);
    if (!res.ok) throw new Error(`the API answered ${res.status}`);
    data = await res.json();
  } catch (e) {
    // An absence WITH ITS REASON, never a cached or illustrative figure.
    status.textContent = `These figures could not be fetched just now (${e}). Nothing is shown rather than something stale.`;
    return;
  }

  const summary = data.summary || {};
  const ranked = Array.isArray(data.ranked) ? data.ranked : [];
  const unranked = Array.isArray(data.unranked) ? data.unranked : [];

  // The fleet, first: every figure below depends on how many boxes saw it.
  const fleet = $("ns-fleet");
  fleet.textContent = "";
  fleet.appendChild(tile("Vantage points", summary.vantages ?? 0, "boxes Helix operates"));
  fleet.appendChild(tile("Targets", summary.targets ?? 0, "hosts measured"));
  // THE RANKING'S OWN SPAN, from its `scope`. summary.first_day/last_day
  // are the whole published history (netstats/routes.ts foldedSpan), so
  // a 7-day table was captioned with a thirteen-day period -- the defect
  // the desktop was fixed for on 2026-10-02.
  const from = data.scope?.from_day;
  const to = data.scope?.to_day;
  fleet.appendChild(tile("Days covered", from && to ? `${from} → ${to}` : "—", "completed days only"));

  // The fleet is too small to rank anything: said above the table.
  hostsNote = Boolean(summary.note);
  $("ns-note").textContent = summary.note || "";
  $("ns-note-card").hidden = !hostsNote || state.view !== "hosts";

  $("ns-weighting").textContent = weightingSentence(summary.vantage_list || []);
  const vlist = $("ns-vantages");
  vlist.textContent = "";
  for (const v of summary.vantage_list || []) {
    vlist.appendChild(
      el("li", {}, [
        el("strong", {}, [String(v.label || v.id || "")]),
        el("span", { class: "muted" }, [
          ` ${[v.city, v.provider, v.country].filter(Boolean).join(" · ")}`,
        ]),
        el("span", { class: "muted" }, [` ×${Number(v.weight ?? 1).toFixed(2)}`]),
      ]),
    );
  }

  $("ns-hosts-title").textContent =
    state.order === "quality" ? "Best score first" : "Most used first";
  $("ns-scope").textContent =
    `Folded over ${data.scope?.days ?? 7} completed days from ` +
    `${data.scope?.vantages_in_scope ?? 0} vantage point` +
    `${(data.scope?.vantages_in_scope ?? 0) === 1 ? "" : "s"}, ` +
    (data.weights_are_default
      ? "using the published default weights."
      : "using custom weights.");

  // THE LIST HAS NO USE DATA YET, said above the table: until enough
  // machines contribute to the host census nothing here is ordered by
  // use, and the page must not read as if it were.
  const popNote = $("ns-popularity-note");
  const pn = data.popularity?.note;
  popNote.hidden = !(pn && state.order === "popularity");
  popNote.textContent = pn || "";

  const rows = $("ns-rows");
  rows.textContent = "";
  // `rows` is the one list since 1.17.0, in the order asked for; the two
  // older lists are read when an older API answers.
  const all = Array.isArray(data.rows) ? data.rows : [...ranked, ...unranked];
  if (all.length === 0) {
    status.textContent =
      "The endpoint answered, but nothing has been published for this period yet.";
    return;
  }
  for (const r of all) {
    const isUnranked = r.rank === null || r.rank === undefined;
    const tr = el("tr", isUnranked ? { class: "muted" } : {});
    const rank = el("td", { class: "num" });
    rank.textContent = isUnranked ? "—" : `#${r.rank}`;
    tr.appendChild(rank);
    // A CONTROL, NOT A READOUT, and shaped as one. The house rule on both
    // surfaces is that the box is the only thing telling the two apart, so
    // a target you can open is a button rather than a clickable-looking
    // span.
    tr.appendChild(
      el("td", {}, [
        el("button", {
          class: "btn btn-sm ghost ns-open",
          type: "button",
          text: String(r.target ?? ""),
          onclick: () => void openAnalysis(String(r.target ?? "")),
        }),
      ]),
    );
    tr.appendChild(
      el("td", {}, [
        [r.org || (r.asn ? `AS${r.asn}` : "—"), r.country].filter(Boolean).join(" · "),
      ]),
    );
    // Used by: the census's distinct-machine count, or "always" for a
    // host on the fleet's list whatever the census says.
    const used = el("td", { class: "num" });
    const contributors = r.popularity?.contributors;
    if (contributors === null || contributors === undefined) {
      used.textContent = "always";
      used.className = "num muted";
      used.title = "On the fleet’s list whatever the census says: a public anchor or a host the Internet Metrics board measures.";
    } else {
      used.textContent = compact(Number(contributors));
      used.title = "distinct machines in the census window";
    }
    tr.appendChild(used);
    const score = el("td", { class: "num" });
    score.textContent =
      r.score === null || r.score === undefined ? "—" : String(Math.round(r.score));
    if (r.quality_rank !== null && r.quality_rank !== undefined) {
      // "#23 of 27", not "23rd": a position means nothing without the
      // size of the list it is a position in.
      score.appendChild(
        el("span", {
          class: "muted ns-qrank",
          text: ` #${r.quality_rank} of ${r.quality_of}`,
          title: "Its position by score",
        }),
      );
    }
    tr.appendChild(score);
    for (const c of COMPONENTS) tr.appendChild(cell((r.components || {})[c]));
    const sub = r.sources?.subscriber;
    const probes = el("td", { class: "num" }, [compact(Number(r.sent ?? 0))]);
    probes.title =
      `vantages: ${Number(r.lost ?? 0).toLocaleString()} lost of ${Number(r.sent ?? 0).toLocaleString()}` +
      (sub
        ? `\nsubscribers: ${Number(sub.lost).toLocaleString()} lost of ${Number(sub.sent).toLocaleString()} from ${sub.contributors} ${Number(sub.contributors) === 1 ? "person" : "people"}` +
          (sub.counted ? ` — counted at ×${sub.weight}` : " — below the floor, not counted")
        : "\nsubscribers: no cell for this period");
    if (sub?.counted) probes.appendChild(el("span", { class: "ns-sub-mark", text: "+" }));
    tr.appendChild(probes);
    tr.appendChild(el("td", { class: "num" }, [String(r.vantages ?? 0)]));
    rows.appendChild(tr);

    // The reason a row carries no rank, on the row it belongs to.
    if (r.rank_note) {
      const why = el("tr");
      why.appendChild(el("td", {}));
      const td = el("td", { class: "muted" }, [String(r.rank_note)]);
      td.colSpan = 11;
      why.appendChild(td);
      rows.appendChild(why);
    }
  }
  status.textContent = "";
}

/// An activity rating as the product renders every rating: a word AND a
/// number AND a colour, never the colour alone.
///
/// THE COLOUR COMES FROM THE API'S WORD, never from a threshold here. This
/// page held its own 80/50 cut while the word came from ratingWord
/// (benchmarks/rating.ts: excellent 85, good 65, usable 40), so a named
/// provider at 47 read "usable" inside a red ✕, and every "good" between 65
/// and 79 drew amber -- on a public ranking. Neither surface holds a
/// threshold; an unknown word is drawn as unknown rather than guessed at.
const RATING_STATUS = { excellent: "ok", good: "ok", usable: "degraded", poor: "down" };

function ratingCell(value, word) {
  if (value === null || value === undefined || !word) return cell(null);
  const status = RATING_STATUS[word] ?? "unknown";
  return el("td", { class: "num" }, [statusPill(status, `${word} ${Math.round(value)}`)]);
}

/* THE ISP TAB. The other question (design §7): how good each provider is
   for the people on it. Nothing here computes a statistic — the fold, the
   floor and the ratings are the API's, and this renders what it sent. */
async function loadIsps() {
  const status = $("ns-isps-status");
  status.textContent = "Fetching the latest provider figures…";
  let data;
  try {
    const res = await fetch(`/v1/netstats/isps?days=${state.days}`);
    if (!res.ok) throw new Error(`the API answered ${res.status}`);
    data = await res.json();
  } catch (e) {
    status.textContent = `These figures could not be fetched just now (${e}). Nothing is shown rather than something stale.`;
    return;
  }
  const isps = Array.isArray(data.isps) ? data.isps : [];
  $("ns-isps-scope").textContent =
    `${data.rated ?? 0} rated of ${isps.length} provider${isps.length === 1 ? "" : "s"} over ` +
    `${data.scope?.days ?? state.days} completed day${(data.scope?.days ?? state.days) === 1 ? "" : "s"}.`;
  const note = $("ns-isps-note");
  note.hidden = !data.note;
  note.textContent = data.note || "";
  $("ns-isps-method").textContent = data.method
    ? `${data.method}. A provider is listed below the floor with its counts and the reason, never hidden. A dead line — every anchor lost at once — is a local fault and is left out before anything is summed.`
    : "";

  const rows = $("ns-isps-rows");
  rows.textContent = "";
  for (const r of isps) {
    const unrated = r.rank === null || r.rank === undefined;
    const tr = el("tr", unrated ? { class: "muted" } : {});
    tr.appendChild(el("td", { class: "num", text: unrated ? "—" : `#${r.rank}` }));
    tr.appendChild(
      el("td", {}, [
        [r.isp || `AS${r.asn}`, `AS${r.asn}`, r.country].filter(Boolean).join(" · "),
      ]),
    );
    tr.appendChild(
      el("td", {
        class: "num",
        text: String(r.contributors ?? 0),
        title: `${r.connections ?? 0} connections, ${r.anchors ?? 0} anchors measured`,
      }),
    );
    tr.appendChild(
      el("td", { class: "num", text: r.score === null || r.score === undefined ? "—" : String(Math.round(r.score)) }),
    );
    tr.appendChild(ratingCell(r.ratings?.video_conf, r.rating_words?.video_conf));
    tr.appendChild(ratingCell(r.ratings?.gaming, r.rating_words?.gaming));
    tr.appendChild(ratingCell(r.ratings?.streaming, r.rating_words?.streaming));
    tr.appendChild(
      el("td", { class: "num", text: r.p50_ms_avg === null || r.p50_ms_avg === undefined ? "—" : `${Number(r.p50_ms_avg).toFixed(1)} ms` }),
    );
    // sent/lost are null below the people floor (netstats/isps.ts withholds
    // every figure computed from fewer than five contributors). A dash, never
    // "0 of 0": an absence is not a zero, and the row's note says why.
    const withheld = r.sent === null || r.sent === undefined || r.lost === null || r.lost === undefined;
    tr.appendChild(
      el("td", {
        class: "num",
        text: withheld
          ? "—"
          : r.loss_pct === null || r.loss_pct === undefined
            ? `${Number(r.lost).toLocaleString()} of ${Number(r.sent).toLocaleString()}`
            : `${Number(r.loss_pct).toFixed(2)}%`,
      }),
    );
    tr.appendChild(el("td", { class: "num", text: withheld ? "—" : compact(Number(r.sent)) }));
    tr.appendChild(el("td", { class: "num", text: String(r.days ?? 0) }));
    rows.appendChild(tr);
    if (r.rank_note) {
      const why = el("tr");
      why.appendChild(el("td", {}));
      const td = el("td", { class: "muted" }, [String(r.rank_note)]);
      td.colSpan = 10;
      why.appendChild(td);
      rows.appendChild(why);
    }
  }
  status.textContent = isps.length === 0 ? "" : "";
}

/* THE ANALYSIS PANEL.
 *
 * Every sentence below is assembled from a field the API sent, including
 * the ones that say a figure is absent. Nothing here computes a statistic
 * of its own: the arithmetic lives in `netstats/analytics.ts` where it is
 * tested, and a second implementation in a page script would be a second
 * chance to disagree with the published number.
 */

/// A number for reading, with its unit, or the em dash that means absent.
function figure(value, unit, digits = 1) {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return "—";
  return `${Number(value).toFixed(digits)} ${unit}`;
}

/// One sentence for a trend, in the register the mission asks for: what
/// happened, over how long, and how far it moved.
function trendSentence(t, unit) {
  if (!t || t.direction === null) return t?.note || "Not enough measured days to read a direction.";
  if (t.direction === "flat") return t.note || "No clear direction over the period.";
  const perWeek = Math.abs(Number(t.slope_per_day) * 7);
  const word = t.direction === "worsening" ? "rose" : "fell";
  return (
    `Over ${t.days} measured days it ${word} by about ` +
    `${perWeek.toFixed(perWeek < 1 ? 2 : 1)} ${unit} a week.`
  );
}

/// A spark of the daily series. Counts of days are what it draws — one
/// point per measured day and a GAP for a day with no reading, because a
/// line drawn straight across a missing day is a reading nobody took.
function spark(days) {
  const width = 320;
  const height = 48;
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("viewBox", `0 0 ${width} ${height}`);
  svg.setAttribute("class", "ns-spark");
  svg.setAttribute("role", "img");
  const values = days.map((d) => d.value).filter((v) => v !== null && Number.isFinite(v));
  if (values.length < 2) return svg;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const x = (i) => (days.length === 1 ? 0 : (i / (days.length - 1)) * (width - 2) + 1);
  const y = (v) => height - 3 - ((v - min) / span) * (height - 6);
  let d = "";
  let pen = false;
  days.forEach((p, i) => {
    if (p.value === null || !Number.isFinite(p.value)) {
      pen = false;
      return;
    }
    d += `${pen ? "L" : "M"}${x(i).toFixed(1)} ${y(p.value).toFixed(1)} `;
    pen = true;
  });
  const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
  path.setAttribute("d", d.trim());
  path.setAttribute("fill", "none");
  path.setAttribute("stroke", "var(--chart-1)");
  path.setAttribute("stroke-width", "1.5");
  path.setAttribute("stroke-linejoin", "round");
  svg.appendChild(path);
  svg.setAttribute(
    "aria-label",
    `${values.length} measured days, from ${min.toFixed(1)} to ${max.toFixed(1)}`,
  );
  return svg;
}

function metricBlock(title, block) {
  const unit = block.unit || "";
  const kids = [el("h4", { text: title }), spark(block.days || [])];

  kids.push(el("p", { text: trendSentence(block.trend, unit) }));

  const shift = block.level_shift || {};
  kids.push(
    el("p", {
      text: shift.day
        ? `The level moved on ${shift.day}: ${figure(shift.before, unit)} before it, ` +
          `${figure(shift.after, unit)} after.`
        : shift.note || "",
    }),
  );

  const o = block.outliers || {};
  if ((o.days || []).length > 0) {
    kids.push(
      el("p", {
        text:
          `${o.days.length} day${o.days.length === 1 ? "" : "s"} stood out against a ` +
          `typical ${figure(o.median, unit)}:`,
      }),
    );
    kids.push(
      el(
        "ul",
        { class: "ns-days" },
        o.days.map((d) =>
          el("li", {
            text: `${d.day} — ${figure(d.value, unit)}, ${d.above ? "above" : "below"} the rest`,
          }),
        ),
      ),
    );
  } else if (o.note) {
    kids.push(el("p", { class: "muted", text: o.note }));
  }

  const co = block.co_movement;
  if (co) {
    kids.push(
      el("p", {
        text: co.note
          ? co.note
          : `${co.shared_days} of this target’s ${co.subject_days} unusual days were ` +
            `also unusual for most of the ${co.peers} targets measured beside it.`,
      }),
    );
  }

  return el("section", { class: "ns-metric" }, kids);
}

async function openAnalysis(target) {
  const card = $("ns-analysis");
  const body = $("ns-an-body");
  card.hidden = false;
  $("ns-an-title").textContent = `Analysis — ${target}`;
  $("ns-an-sub").textContent = "Reading the published daily rows…";
  body.textContent = "";
  card.scrollIntoView({ block: "nearest" });

  let data;
  try {
    const res = await fetch(`/v1/netstats/target/${encodeURIComponent(target)}/analysis`);
    if (!res.ok) throw new Error(`the API answered ${res.status}`);
    data = await res.json();
  } catch (e) {
    // An absence with its reason, never a cached or illustrative figure.
    $("ns-an-sub").textContent =
      `This analysis could not be fetched just now (${e}). Nothing is shown rather than something stale.`;
    return;
  }

  $("ns-an-sub").textContent =
    `Over the last ${data.period_days} completed days, in the ` +
    `${data.band || "unclassified"} distance band.`;
  body.appendChild(metricBlock("Round-trip time", data.latency || {}));
  body.appendChild(metricBlock("Loss", data.loss || {}));

  const list = $("ns-an-method-list");
  list.textContent = "";
  for (const [key, value] of Object.entries(data.method || {})) {
    list.appendChild(el("dt", { text: key.replace(/_/g, " ") }));
    list.appendChild(el("dd", { text: String(value) }));
  }
}

$("ns-an-close").addEventListener("click", () => {
  $("ns-analysis").hidden = true;
});

bindSeg("data-ns-view", "view", () => {
  showView();
  if (state.view === "isps") void loadIsps();
});
bindSeg("data-ns-days", "days", () => {
  state.days = Number(state.days);
  void load();
  if (state.view === "isps") void loadIsps();
});
bindSeg("data-ns-order", "order", () => void load());

showView();
void load();
